import * as React from 'react'

import {
  DragLayerMonitorProps,
  NodeModel,
  Tree as ReactDndTree,
} from '@minoru/react-dnd-treeview'
import {
  ChevronsDownUp,
  ChevronsUpDown,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react'

import { IPermitTypeField, PermitFieldType } from '~/client/graph'
import { getFieldTypeCaption } from '~/client/src/desktop/constants/formFieldsCaptionMap'
import { Button } from '~/client/src/shared/components/Button/Button'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { expandablePermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FieldConfigurator from '../../FieldConfigurator/FieldConfigurator'
import FormFieldsModal from '../../FormFieldsModal'
import { TABLE_ROW_CONTROL_REVEAL } from './rowControlReveal'

const SECTION_TREE_ROOT_ID = 'section-field-list-root'

interface IProps {
  /** Title shown in the collapsible header (e.g. "3 Associated Fields"). */
  title: string
  /** Label shown when the list is empty (no fields configured yet). */
  emptyLabel: string
  /** Add-button text (e.g. "Add associated field", "Add table column"). */
  addButtonLabel: string
  fields: IPermitTypeField[]
  restrictedFieldTypes: PermitFieldType[]
  /** Optional cap on the number of fields (Table caps at 15). */
  maxFields?: number

  /** Insert immediately after `index`. Pass `fields.length - 1` to append. */
  onAddField(index: number, fieldType: PermitFieldType): void
  onUpdateField(index: number, field: IPermitTypeField): void
  onRemoveField(field: IPermitTypeField): void
  /** Commit a reorder once the user finishes dragging. */
  onReorderField?(fromIndex: number, toIndex: number): void

  /** Forwarded to inner FieldConfigurator so nested Table cells still render. */
  tableConfiguratorRenderer?(field: IPermitTypeField): JSX.Element
}

/**
 * Shared "section with collapsible title + list of inline-editable fields"
 * used by both the Table-columns configurator and the MaterialResource
 * associated-fields configurator.
 *
 * Reordering uses `@minoru/react-dnd-treeview`'s flat-tree mode — same
 * library + idiom as the outer FormConfigurator field tree, so DnD behavior
 * stays consistent across the configurator. When `onReorderField` is
 * provided AND there are 2+ rows, each row becomes a draggable tree node;
 * the grip handle is the visual affordance but the whole row is the drag
 * source (matches the outer tree).
 */
function SectionFieldList({
  title,
  emptyLabel,
  addButtonLabel,
  fields,
  restrictedFieldTypes,
  maxFields,
  onAddField,
  onUpdateField,
  onRemoveField,
  onReorderField,
  tableConfiguratorRenderer,
}: IProps): JSX.Element {
  const canAdd = maxFields === undefined || fields.length < maxFields
  const isReorderEnabled = !!onReorderField && fields.length > 1
  const hasExpandableFields = fields.some(f =>
    expandablePermitFieldTypes.includes(f.type),
  )

  const [allSettingsCollapsed, setAllSettingsCollapsed] = React.useState(true)
  const [fieldOverrides, setFieldOverrides] = React.useState<
    Map<string, boolean>
  >(() => new Map())

  const toggleAllSettings = React.useCallback(() => {
    setAllSettingsCollapsed(prev => !prev)
    setFieldOverrides(new Map())
  }, [])

  const toggleFieldExpansion = React.useCallback(
    (fieldId: string) => {
      setFieldOverrides(prev => {
        const next = new Map(prev)
        const currentlyCollapsed = next.has(fieldId)
          ? next.get(fieldId)
          : allSettingsCollapsed
        next.set(fieldId, !currentlyCollapsed)
        return next
      })
    },
    [allSettingsCollapsed],
  )

  const isFieldCollapsed = React.useCallback(
    (fieldId: string): boolean => {
      return fieldOverrides.has(fieldId)
        ? fieldOverrides.get(fieldId)
        : allSettingsCollapsed
    },
    [allSettingsCollapsed, fieldOverrides],
  )

  const treeData = React.useMemo<NodeModel<IPermitTypeField>[]>(
    () =>
      fields.map(field => ({
        id: field.id,
        parent: SECTION_TREE_ROOT_ID,
        text: field.id,
        droppable: false,
        data: field,
      })),
    [fields],
  )

  const handleDrop = React.useCallback(
    (newTree: NodeModel<IPermitTypeField>[]) => {
      if (!onReorderField) return
      const oldOrder = fields.map(f => f.id)
      const newOrder = newTree.map(n => String(n.id))
      if (oldOrder.length !== newOrder.length) return

      // A single drag moves one item from oldIdx to newIdx and shifts
      // everything in between by one.  The moved item is the one whose
      // displacement is largest (all shifted items move by exactly ±1).
      const newIndexOf = new Map(newOrder.map((id, i) => [id, i]))
      let movedId: string | null = null
      let maxDist = 0
      for (let oldIdx = 0; oldIdx < oldOrder.length; oldIdx++) {
        const id = oldOrder[oldIdx]
        const dist = Math.abs(newIndexOf.get(id) - oldIdx)
        if (dist > maxDist) {
          maxDist = dist
          movedId = id
        }
      }
      if (movedId) {
        const fromIdx = oldOrder.indexOf(movedId)
        const toIdx = newIndexOf.get(movedId)
        if (fromIdx !== toIdx) {
          onReorderField(fromIdx, toIdx)
        }
      }
    },
    [fields, onReorderField],
  )

  const renderNodeRow = React.useCallback(
    (node: NodeModel<IPermitTypeField>): JSX.Element | null => {
      const field = node.data
      if (!field) return null
      const idx = fields.findIndex(f => f.id === field.id)
      return (
        <div className="row x-center y-center tw:gap-1 tw:group/table-row">
          <div className="tw:flex tw:size-7 tw:flex-none! tw:items-center">
            {canAdd && (
              <FormFieldsModal
                addNewField={(type: PermitFieldType) => onAddField(idx, type)}
                restrictedFields={restrictedFieldTypes}
              >
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className={TABLE_ROW_CONTROL_REVEAL}
                  aria-label={addButtonLabel}
                >
                  <PlusIcon />
                </Button>
              </FormFieldsModal>
            )}
          </div>
          <FieldConfigurator
            field={field}
            isMultipleToggleHidden={true}
            isCollapsed={isFieldCollapsed(field.id)}
            onCollapseToggle={() => toggleFieldExpansion(field.id)}
            onChange={updated => onUpdateField(idx, updated)}
            tableConfiguratorRenderer={tableConfiguratorRenderer}
          />
          <Button
            variant="destructive-ghost"
            size="icon-sm"
            aria-label={Localization.translator.delete}
            className={`${TABLE_ROW_CONTROL_REVEAL} tw:flex-none!`}
            onMouseDown={e => e.preventDefault()}
            onClick={() => onRemoveField(field)}
          >
            <Trash2Icon />
          </Button>
        </div>
      )
    },
    [
      fields,
      canAdd,
      restrictedFieldTypes,
      tableConfiguratorRenderer,
      isFieldCollapsed,
      toggleFieldExpansion,
      onAddField,
      onUpdateField,
      onRemoveField,
    ],
  )

  const renderPlaceholder = React.useCallback(
    (): JSX.Element => (
      <div
        className="absolute bg-color-blue-brand drag-placeholder brada4"
        style={{ left: 0 }}
      />
    ),
    [],
  )

  const renderDragPreview = React.useCallback(
    ({ item }: DragLayerMonitorProps<IPermitTypeField>): JSX.Element | null => {
      if (!item.data) return null
      const { type, caption } = item.data
      return (
        <div className="row tw:gap-2.5 bg-grey brada10 mw160 h36 px10 text large bold">
          <PermitFieldIcon fieldName={type} />
          <span className="text-ellipsis">
            {caption || getFieldTypeCaption(type)}
          </span>
        </div>
      )
    },
    [],
  )

  const addButton = canAdd ? (
    <FormFieldsModal
      addNewField={(type: PermitFieldType) =>
        onAddField(fields.length - 1, type)
      }
      restrictedFields={restrictedFieldTypes}
    >
      <Button
        variant="ghost"
        className="tw:w-fit tw:text-muted-foreground"
        size="lg"
      >
        <AddIcon data-icon="inline-start" size="xs" />
        {addButtonLabel}
      </Button>
    </FormFieldsModal>
  ) : null

  return (
    <div>
      <div className="tw:flex tw:items-center tw:gap-2 tw:py-3 tw:pl-6">
        {fields.length > 0 && hasExpandableFields && (
          <Button variant="ghost" size="icon-xs" onClick={toggleAllSettings}>
            {allSettingsCollapsed ? (
              <ChevronsUpDown className="tw:size-4" />
            ) : (
              <ChevronsDownUp className="tw:size-4" />
            )}
          </Button>
        )}
        <span className="tw:text-sm tw:font-medium tw:leading-5 tw:text-muted-foreground">
          {fields.length > 0 ? title : emptyLabel}:
        </span>
      </div>
      <div>
        {fields.length > 0 && (
          <div>
            <ReactDndTree
              classes={{
                root: 'relative ma0 tw:list-none tw:p-0',
                draggingSource: 'opacity3',
                placeholder: 'relative',
                dropTarget: 'bg-palette-brand-lightest brada4',
              }}
              rootId={SECTION_TREE_ROOT_ID}
              tree={treeData}
              sort={false}
              insertDroppableFirst={false}
              dropTargetOffset={10}
              canDrop={() => isReorderEnabled}
              render={renderNodeRow}
              placeholderRender={renderPlaceholder}
              dragPreviewRender={renderDragPreview}
              onDrop={handleDrop}
            />
          </div>
        )}
        <div className="tw:pl-6">{addButton}</div>
      </div>
    </div>
  )
}

export default SectionFieldList
